// src/pages/calendar/CalendarPage.tsx -- Phase 9 / Cut 2.
//
// Calendar root: filter strip, view toggle (List / Month), persisted tweaks,
// and detail-screen handoff. Live data via GET /api/calendar/events; mock
// seed is gone. Tweak state round-trips through PUT /api/me/preferences so
// the user's view + show-past choice survives a reload.
//
// Cut 2 changes (vs Cut 1, see PR #4):
//   * MOCK_EVENTS deleted; events state populated from /api/calendar/events
//     (full list each load -- no since= filter in V1; payload stays small
//     because the list endpoint omits per-event schedule).
//   * Loading skeleton + error card + empty card + syncedAt footer match
//     the WeeklyPage pattern.
//   * Tweaks read from me.preferences.calendar at mount; on change, a 500ms
//     debounce fires PUT /api/me/preferences merging only the calendar
//     sub-object (theme/logo preserved).
//   * EventDetail navigation now passes the event id only; EventDetail
//     fetches the full row with schedule on mount.
//
// Carry-forwards from Cut 1:
//   * TODAY computed Europe/London-aware via Intl.DateTimeFormat("en-CA").
//   * Category lookups use the 9-EventType CAL_CATEGORIES map keyed on the
//     EventType.name verbatim.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../../api";
import { useCurrentUser } from "../../auth";
import { C, F } from "../../theme";
import { Icons, Stamp, TopBar, Eyebrow } from "../../components/ui";
import {
  CAL_CATEGORIES,
  CAL_CATEGORY_LIST,
  TONES,
  safeTone,
  type CalCategory,
} from "./categories";
import type { CalendarEvent } from "./types";
import { EventDetail } from "./EventDetail";

interface CalendarListResponse {
  syncedAt: string | null;
  events: CalendarEvent[];
}

const MONTH_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const MONTH_CELL_H = 72;
const MAX_TRACKS = 3;
const PAST_HORIZON_DAYS = 30;

type CalendarView = "list" | "month";

interface CalendarTweaks {
  defaultView: CalendarView;
  showPast: boolean;
}

const TWEAK_DEFAULTS: CalendarTweaks = {
  defaultView: "list",
  showPast: true,
};

// ---------------------------------------------------------------------------
// Date helpers (Europe/London-aware where it matters)
// ---------------------------------------------------------------------------

/** "YYYY-MM-DD" today key in Europe/London. Memoised at mount so a 24-hour
 *  session doesn't tick over without a reload (acceptable per gotcha #5). */
function useTodayKey(): string {
  return useMemo(() => {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return fmt.format(new Date());
  }, []);
}

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function addDays(iso: string, delta: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return dayKey(d);
}

// ---------------------------------------------------------------------------
// Filter strip
// ---------------------------------------------------------------------------

function FilterChip({
  active, label, tone, count, onClick,
}: {
  active: boolean;
  label: string;
  tone?: CalCategory["tone"];
  count: number;
  onClick: () => void;
}) {
  const t = tone ? TONES[safeTone(tone)] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 12px",
        borderRadius: 11,
        whiteSpace: "nowrap",
        border: active ? `1.5px solid ${t ? t.solid : C.brassDk}` : `1px solid ${C.hairlineLt}`,
        background: active ? (t ? t.bg : C.brassBg) : C.card,
        color: active ? (t ? t.fg : C.brassDk) : C.inkSoft,
        fontFamily: F.body,
        fontWeight: 700,
        fontSize: 12.5,
        cursor: "pointer",
        boxShadow: active ? C.shadowS : "none",
      }}
    >
      {tone && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            background: t ? t.solid : C.brassDk,
          }}
        />
      )}
      {label}
      <span
        style={{
          fontFamily: F.mono,
          fontSize: 10.5,
          fontWeight: 800,
          color: active ? (t ? t.fg : C.brassDk) : C.inkMute,
          letterSpacing: 0.4,
        }}
      >
        {count}
      </span>
    </button>
  );
}

function FilterStrip({
  activeCats, setActiveCats, events,
}: {
  activeCats: Set<string>;
  setActiveCats: (next: Set<string>) => void;
  events: ReadonlyArray<CalendarEvent>;
}) {
  const allCount = events.length;
  const isAll = activeCats.size === 0;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        overflowX: "auto",
        margin: "0 -16px",
        padding: "2px 16px 6px",
        scrollbarWidth: "none",
      }}
    >
      <FilterChip
        active={isAll}
        label="All"
        count={allCount}
        onClick={() => setActiveCats(new Set())}
      />
      {CAL_CATEGORY_LIST.map((c) => {
        const count = events.filter((e) => e.category === c.id).length;
        if (count === 0) return null;
        // Show the part before the " / " on the chip, full name in the
        // category bullet on each event row.
        const shortLabel = c.label.split(" / ")[0];
        return (
          <FilterChip
            key={c.id}
            active={!isAll && activeCats.has(c.id)}
            label={shortLabel}
            tone={c.tone}
            count={count}
            onClick={() => {
              const next = new Set(activeCats);
              if (next.has(c.id)) next.delete(c.id);
              else next.add(c.id);
              setActiveCats(next);
            }}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View toggle (List | Month)
// ---------------------------------------------------------------------------

// List icon -- production Icons exports Cal but not a horizontal-list shape.
// Inlined here rather than added to the global Icon set; if a second view
// needs it, promote into src/components/ui/Icon.tsx.
function ListIcon({ s = 15, c = C.inkMute }: { s?: number; c?: string }) {
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </svg>
  );
}

function ViewToggle({
  view, setView,
}: {
  view: CalendarView;
  setView: (v: CalendarView) => void;
}) {
  const items: ReadonlyArray<{
    k: CalendarView;
    l: string;
    Ic: (props: { s?: number; c?: string }) => JSX.Element;
  }> = [
    { k: "list", l: "List", Ic: ListIcon },
    { k: "month", l: "Month", Ic: Icons.Cal },
  ];
  return (
    <div
      role="tablist"
      aria-label="Calendar view"
      style={{
        display: "flex",
        background: C.paperWarm,
        padding: 4,
        borderRadius: 13,
        border: `1px solid ${C.hairlineLt}`,
        gap: 2,
      }}
    >
      {items.map(({ k, l, Ic }) => {
        const on = view === k;
        return (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => setView(k)}
            style={{
              flex: 1,
              padding: "9px 10px",
              border: "none",
              borderRadius: 9,
              background: on ? C.card : "transparent",
              color: on ? C.brassDk : C.inkSoft,
              fontFamily: F.body,
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: 0.3,
              cursor: "pointer",
              boxShadow: on ? C.shadowS : "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Ic s={15} c={on ? C.brassDk : C.inkMute} />
            {l}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List view: one row per event, grouped by month
// ---------------------------------------------------------------------------

function EventListRow({
  event, today, onTap,
}: {
  event: CalendarEvent;
  today: string;
  onTap: (e: CalendarEvent) => void;
}) {
  const cat = CAL_CATEGORIES[event.category];
  const tone = cat ? safeTone(cat.tone) : "brass";
  const t = TONES[tone];
  const isPast = event.end < today;
  const isLive = event.start <= today && today <= event.end;
  const start = new Date(event.start + "T00:00:00");
  const sameMonth = event.start.slice(0, 7) === event.end.slice(0, 7);
  const sd = start.getDate();
  const ed = new Date(event.end + "T00:00:00").getDate();
  return (
    <button
      type="button"
      onClick={() => onTap(event)}
      style={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "58px 1fr 18px",
        gap: 12,
        alignItems: "center",
        padding: "14px 14px",
        background: C.card,
        border: `1px solid ${C.hairlineLt}`,
        borderRadius: 14,
        boxShadow: C.shadowS,
        cursor: "pointer",
        textAlign: "left",
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        position: "relative",
        overflow: "hidden",
        opacity: isPast ? 0.72 : 1,
      }}
    >
      {/* Left category stripe */}
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: t.solid }} />
      {/* Date block */}
      <div style={{ textAlign: "center", paddingLeft: 4 }}>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 9.5,
            fontWeight: 800,
            color: t.fg,
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          {start.toLocaleDateString("en-GB", { month: "short" })}
        </div>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 24,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: -0.5,
            lineHeight: 1,
            marginTop: 2,
          }}
        >
          {sameMonth && sd !== ed ? `${sd}–${ed}` : sd}
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 9.5,
            fontWeight: 800,
            color: C.inkMute,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            marginTop: 3,
          }}
        >
          {start.toLocaleDateString("en-GB", { weekday: "short" })}
        </div>
      </div>
      {/* Event copy */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4, flexWrap: "wrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 7px",
              borderRadius: 5,
              background: t.bg,
              color: t.fg,
              fontFamily: F.body,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              border: `1px solid ${t.bd}`,
            }}
          >
            <span style={{ width: 5, height: 5, borderRadius: 5, background: t.solid }} />
            {cat ? cat.label.split(" / ")[0] : event.category}
          </span>
          {isLive && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 7px",
                borderRadius: 5,
                background: TONES.sage.bg,
                color: TONES.sage.fg,
                fontFamily: F.body,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                border: `1px solid ${TONES.sage.bd}`,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 5,
                  background: TONES.sage.solid,
                  boxShadow: `0 0 6px ${TONES.sage.solid}`,
                }}
              />
              Live
            </span>
          )}
          {isPast && <Stamp tone="paper">Past</Stamp>}
        </div>
        <div
          style={{
            fontFamily: F.serif,
            fontSize: 16.5,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: -0.2,
            lineHeight: 1.15,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {event.name}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: C.inkSoft,
            fontWeight: 600,
            marginTop: 3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {event.location.split("·")[0].trim()}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
          <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.brassDk, letterSpacing: 0.3 }}>
            {event.operatingDays} day{event.operatingDays === 1 ? "" : "s"}
          </span>
          <span style={{ color: C.inkMute, fontWeight: 700, fontSize: 10 }}>·</span>
          <span style={{ fontFamily: F.mono, fontSize: 11, fontWeight: 700, color: C.inkSoft, letterSpacing: 0.3 }}>
            {event.capacity > 0 ? `${event.capacity.toLocaleString()} cap` : "no services"}
          </span>
        </div>
      </div>
      <Icons.Chev s={18} c={C.inkMute} />
    </button>
  );
}

function ListView({
  events, today, onTapEvent,
}: {
  events: ReadonlyArray<CalendarEvent>;
  today: string;
  onTapEvent: (e: CalendarEvent) => void;
}) {
  const months = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {};
    events.forEach((e) => {
      const k = e.start.slice(0, 7);
      (grouped[k] = grouped[k] ?? []).push(e);
    });
    return grouped;
  }, [events]);

  const monthKeys = Object.keys(months).sort();
  const upcomingIdx = monthKeys.findIndex((k) => k >= today.slice(0, 7));

  if (monthKeys.length === 0) {
    return (
      <div style={{ padding: "40px 0", textAlign: "center" }}>
        <Icons.Cal s={32} c={C.inkMute} />
        <div
          style={{
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 15,
            color: C.inkMute,
            marginTop: 10,
          }}
        >
          No events match your filter
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {monthKeys.map((mk, mi) => {
        const monthDate = new Date(mk + "-01T00:00:00");
        const monthLabel = monthDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
        const [monthName, monthYear] = monthLabel.split(" ");
        const isCurrent = mi === upcomingIdx;
        return (
          <div key={mk}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10, paddingLeft: 2 }}>
              <div
                style={{
                  fontFamily: F.serif,
                  fontSize: 22,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: -0.4,
                  lineHeight: 1,
                }}
              >
                {monthName}
              </div>
              <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 14, fontWeight: 600, color: C.brassDk }}>
                {monthYear}
              </div>
              {isCurrent && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "2px 7px",
                    borderRadius: 5,
                    background: C.brassBg,
                    color: C.brassDk,
                    fontFamily: F.body,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    border: "1px solid rgba(167,117,28,0.25)",
                  }}
                >
                  This month
                </span>
              )}
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: `linear-gradient(90deg, ${C.hairline}, transparent)`,
                  marginLeft: 6,
                }}
              />
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.inkMute,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {months[mk].length} event{months[mk].length === 1 ? "" : "s"}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {months[mk].map((e) => (
                <EventListRow key={e.id} event={e} today={today} onTap={onTapEvent} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month view -- 6-week grid with absolutely-positioned pill bars (gotcha #3)
// ---------------------------------------------------------------------------

interface WeekSegment {
  event: CalendarEvent;
  startCol: number;
  span: number;
  track: number;
}

function monthGrid(year: number, month0: number): Date[] {
  const first = new Date(year, month0, 1);
  const dow = (first.getDay() + 6) % 7; // 0 = Mon
  const start = new Date(year, month0, 1 - dow);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/** Per-week pass: which events touch this week, what start column / span, and
 *  what stack track. Mirrors prototype's buildWeekTracks exactly. */
function buildWeekTracks(
  cells: Date[],
  events: ReadonlyArray<CalendarEvent>,
): WeekSegment[][] {
  const weeks: WeekSegment[][] = [];
  for (let w = 0; w < 6; w++) {
    const weekStart = cells[w * 7];
    const weekEnd = cells[w * 7 + 6];
    const segs: WeekSegment[] = [];
    events.forEach((ev) => {
      const evS = new Date(ev.start + "T00:00:00");
      const evE = new Date(ev.end + "T00:00:00");
      if (evE < weekStart || evS > weekEnd) return;
      const startCol = Math.max(0, Math.floor((evS.getTime() - weekStart.getTime()) / 86400000));
      const endCol = Math.min(6, Math.floor((evE.getTime() - weekStart.getTime()) / 86400000));
      segs.push({ event: ev, startCol, span: endCol - startCol + 1, track: 0 });
    });
    // Assign tracks (no two overlapping segments on the same track).
    segs.sort((a, b) => a.startCol - b.startCol || b.span - a.span);
    const tracks: Array<Array<{ startCol: number; endCol: number }>> = [];
    segs.forEach((s) => {
      let tt = 0;
      while (true) {
        if (!tracks[tt]) {
          tracks[tt] = [];
          break;
        }
        const conflict = tracks[tt].some(
          (x) => !(s.startCol + s.span - 1 < x.startCol || s.startCol > x.endCol),
        );
        if (!conflict) break;
        tt++;
      }
      tracks[tt].push({ startCol: s.startCol, endCol: s.startCol + s.span - 1 });
      s.track = tt;
    });
    weeks.push(segs);
  }
  return weeks;
}

function MonthCell({
  date, currentMonth, today, selected, onClick,
}: {
  date: Date;
  currentMonth: number;
  today: string;
  selected: boolean;
  onClick: () => void;
}) {
  const inMonth = date.getMonth() === currentMonth;
  const isToday = dayKey(date) === today;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: MONTH_CELL_H,
        background: "transparent",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        padding: "4px 4px 0",
        position: "relative",
        WebkitTapHighlightColor: "transparent",
        borderRight: `1px solid ${C.hairlineLt}`,
        borderBottom: `1px solid ${C.hairlineLt}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <span
        style={{
          fontFamily: F.body,
          fontSize: 12,
          fontWeight: 800,
          color: !inMonth ? C.inkMute : isToday ? C.brassGlow : C.ink,
          opacity: inMonth ? 1 : 0.45,
          letterSpacing: 0.2,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: 11,
          background: isToday ? C.brassDk : selected ? C.brassBg : "transparent",
          marginLeft: 2,
        }}
      >
        {date.getDate()}
      </span>
    </button>
  );
}

function MonthView({
  events, today, onTapEvent,
}: {
  events: ReadonlyArray<CalendarEvent>;
  today: string;
  onTapEvent: (e: CalendarEvent) => void;
}) {
  const [cursor, setCursor] = useState<{ y: number; m: number }>(() => {
    const d = new Date(today + "T00:00:00");
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const [selected, setSelected] = useState<string | null>(null);

  const cells = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor]);
  const weeks = useMemo(() => buildWeekTracks(cells, events), [cells, events]);
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
  const [monthName] = monthLabel.split(" ");

  function shift(delta: number) {
    let y = cursor.y;
    let m = cursor.m + delta;
    if (m < 0) {
      m += 12;
      y -= 1;
    }
    if (m > 11) {
      m -= 12;
      y += 1;
    }
    setCursor({ y, m });
    setSelected(null);
  }

  const selectedEvents = useMemo(() => {
    if (!selected) return [];
    return events
      .filter((e) => e.start <= selected && selected <= e.end)
      .slice() // copy before sort (readonly array)
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [selected, events]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Month nav */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => shift(-1)}
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            border: `1px solid ${C.hairline}`,
            background: C.card,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: C.shadowS,
          }}
        >
          {/* Production Icons only exports a right-chevron; flip horizontally
              for the previous-month button. */}
          <span style={{ display: "inline-flex", transform: "scaleX(-1)" }}>
            <Icons.Chev s={18} c={C.brassDk} />
          </span>
        </button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div
            style={{
              fontFamily: F.body,
              fontSize: 10.5,
              fontWeight: 800,
              color: C.inkMute,
              letterSpacing: 1.5,
              textTransform: "uppercase",
            }}
          >
            {cursor.y}
          </div>
          <div
            style={{
              fontFamily: F.serif,
              fontSize: 22,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: -0.4,
              lineHeight: 1,
            }}
          >
            {monthName}
          </div>
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => shift(1)}
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            border: `1px solid ${C.hairline}`,
            background: C.card,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: C.shadowS,
          }}
        >
          <Icons.Chev s={18} c={C.brassDk} />
        </button>
      </div>

      {/* Calendar grid */}
      <div
        style={{
          background: C.card,
          borderRadius: 14,
          overflow: "hidden",
          border: `1px solid ${C.hairlineLt}`,
          boxShadow: C.shadowS,
          position: "relative",
        }}
      >
        {/* Weekday header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            background: C.paperWarm,
            borderBottom: `1px solid ${C.hairline}`,
          }}
        >
          {MONTH_WEEKDAYS.map((d, i) => (
            <div
              key={i}
              style={{
                padding: "8px 0",
                textAlign: "center",
                fontFamily: F.body,
                fontSize: 10.5,
                fontWeight: 800,
                color: C.inkSoft,
                letterSpacing: 1,
                textTransform: "uppercase",
                borderRight: i < 6 ? `1px solid ${C.hairlineLt}` : "none",
              }}
            >
              {d}
            </div>
          ))}
        </div>
        {/* Day cells (6 weeks) with absolutely-positioned pill bars on top */}
        <div style={{ position: "relative" }}>
          {[0, 1, 2, 3, 4, 5].map((w) => (
            <div
              key={w}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                position: "relative",
              }}
            >
              {[0, 1, 2, 3, 4, 5, 6].map((c) => {
                const date = cells[w * 7 + c];
                const k = dayKey(date);
                return (
                  <MonthCell
                    key={c}
                    date={date}
                    currentMonth={cursor.m}
                    today={today}
                    selected={selected === k}
                    onClick={() => setSelected(selected === k ? null : k)}
                  />
                );
              })}
              {/* Pill bars overlay (this week) */}
              <div style={{ position: "absolute", left: 0, right: 0, top: 22, pointerEvents: "none" }}>
                {weeks[w].slice(0, MAX_TRACKS).map((seg, si) => {
                  const cat = CAL_CATEGORIES[seg.event.category];
                  const tone = cat ? safeTone(cat.tone) : "brass";
                  const t = TONES[tone];
                  const segS = new Date(seg.event.start + "T00:00:00");
                  const segE = new Date(seg.event.end + "T00:00:00");
                  const weekStart = cells[w * 7];
                  const weekEnd = cells[w * 7 + 6];
                  const contLeft = segS < weekStart;
                  const contRight = segE > weekEnd;
                  const pillStyle: CSSProperties = {
                    position: "absolute",
                    left: `calc(${(seg.startCol / 7) * 100}% + 3px)`,
                    width: `calc(${(seg.span / 7) * 100}% - 6px)`,
                    top: seg.track * 14,
                    height: 12,
                    background: t.solid,
                    borderRadius: 4,
                    borderTopLeftRadius: contLeft ? 0 : 4,
                    borderBottomLeftRadius: contLeft ? 0 : 4,
                    borderTopRightRadius: contRight ? 0 : 4,
                    borderBottomRightRadius: contRight ? 0 : 4,
                    paddingLeft: 6,
                    paddingRight: 4,
                    display: "flex",
                    alignItems: "center",
                    pointerEvents: "auto",
                    cursor: "pointer",
                    overflow: "hidden",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  };
                  return (
                    <button
                      key={si}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTapEvent(seg.event);
                      }}
                      style={{ ...pillStyle, border: "none" }}
                    >
                      <span
                        style={{
                          fontFamily: F.body,
                          fontSize: 9,
                          fontWeight: 800,
                          color: t.solidFg,
                          letterSpacing: 0.4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {seg.event.name}
                      </span>
                    </button>
                  );
                })}
                {weeks[w].length > MAX_TRACKS && (
                  <div
                    style={{
                      position: "absolute",
                      left: "calc(100% - 36px)",
                      top: MAX_TRACKS * 14,
                      fontFamily: F.body,
                      fontSize: 9,
                      fontWeight: 800,
                      color: C.inkSoft,
                      letterSpacing: 0.3,
                      background: C.paperSoft,
                      border: `1px solid ${C.hairlineLt}`,
                      padding: "1px 5px",
                      borderRadius: 4,
                    }}
                  >
                    +{weeks[w].length - MAX_TRACKS}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Day drawer */}
      {selected && (
        <div
          style={{
            background: C.card,
            border: `1px solid ${C.hairlineLt}`,
            borderRadius: 14,
            boxShadow: C.shadowM,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 16px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${C.hairlineLt}`,
            }}
          >
            <div>
              <Eyebrow>
                {new Date(selected + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long" })}
              </Eyebrow>
              <div
                style={{
                  fontFamily: F.serif,
                  fontSize: 20,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: -0.3,
                  marginTop: 2,
                  lineHeight: 1.1,
                }}
              >
                {new Date(selected + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
              </div>
            </div>
            <Stamp tone="brass">
              {selectedEvents.length} event{selectedEvents.length === 1 ? "" : "s"}
            </Stamp>
          </div>
          {selectedEvents.length === 0 ? (
            <div style={{ padding: "22px 16px", textAlign: "center" }}>
              <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 14, color: C.inkMute }}>
                Nothing on this day
              </div>
            </div>
          ) : (
            <div style={{ padding: "4px 12px 12px" }}>
              {selectedEvents.map((e) => {
                const cat = CAL_CATEGORIES[e.category];
                const tone = cat ? safeTone(cat.tone) : "brass";
                const t = TONES[tone];
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onTapEvent(e)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 8px",
                      background: "transparent",
                      border: "none",
                      borderRadius: 10,
                      cursor: "pointer",
                      textAlign: "left",
                      WebkitTapHighlightColor: "transparent",
                      borderTop: `1px solid ${C.hairlineLt}`,
                    }}
                  >
                    <span style={{ width: 4, height: 34, borderRadius: 2, background: t.solid, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontFamily: F.serif,
                          fontSize: 15,
                          fontWeight: 700,
                          color: C.ink,
                          letterSpacing: -0.2,
                          lineHeight: 1.15,
                        }}
                      >
                        {e.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: C.inkSoft,
                          fontWeight: 700,
                          marginTop: 2,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        {cat ? cat.label.split(" / ")[0] : e.category}
                        {" · "}
                        {fmtShortRange(e.start, e.end)}
                      </div>
                    </div>
                    <Icons.Chev s={16} c={C.inkMute} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Used inside the day drawer for compact event-row dates. Same logic as
 *  EventDetail's fmtRange, kept local because the day-drawer's contracted
 *  context already implies the year. */
function fmtShortRange(start: string, end: string): string {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const sd = s.getDate();
  const ed = e.getDate();
  const sm = s.toLocaleDateString("en-GB", { month: "short" });
  const em = e.toLocaleDateString("en-GB", { month: "short" });
  if (start === end) return `${sd} ${sm}`;
  if (sm === em) return `${sd}–${ed} ${sm}`;
  return `${sd} ${sm} – ${ed} ${em}`;
}

// ---------------------------------------------------------------------------
// Inline Tweaks panel (session-only). Cut 2 replaces this with persistent
// preferences via PUT /api/me/preferences (kickoff §Cut 2).
// ---------------------------------------------------------------------------

function TweaksPopover({
  tweaks, setTweaks, onClose,
}: {
  tweaks: CalendarTweaks;
  setTweaks: (next: CalendarTweaks) => void;
  onClose: () => void;
}) {
  // Click-outside closes; tap on the overlay or a navigate-away unmounts.
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Calendar tweaks"
      style={{
        position: "absolute",
        top: "100%",
        right: 16,
        marginTop: 6,
        width: 260,
        background: C.card,
        border: `1px solid ${C.hairlineLt}`,
        borderRadius: 12,
        boxShadow: C.shadowM,
        padding: "12px 14px",
        zIndex: 60,
      }}
    >
      <Eyebrow>Tweaks</Eyebrow>
      <div
        style={{
          fontSize: 11,
          color: C.inkMute,
          fontFamily: F.body,
          fontWeight: 600,
          marginTop: 2,
          marginBottom: 10,
          lineHeight: 1.4,
        }}
      >
        Saved to your account.
      </div>

      <div
        style={{
          fontFamily: F.body,
          fontSize: 11,
          fontWeight: 800,
          color: C.inkSoft,
          letterSpacing: 1,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        Default view
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {(["list", "month"] as const).map((v) => {
          const on = tweaks.defaultView === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setTweaks({ ...tweaks, defaultView: v })}
              style={{
                flex: 1,
                padding: "7px 10px",
                borderRadius: 8,
                border: `1px solid ${on ? C.brassDk : C.hairline}`,
                background: on ? C.brassBg : C.card,
                color: on ? C.brassDk : C.inkSoft,
                fontFamily: F.body,
                fontWeight: 800,
                fontSize: 12,
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {v}
            </button>
          );
        })}
      </div>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          cursor: "pointer",
        }}
      >
        <span
          style={{
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 700,
            color: C.ink,
          }}
        >
          Show past events
        </span>
        <input
          type="checkbox"
          checked={tweaks.showPast}
          onChange={(e) => setTweaks({ ...tweaks, showPast: e.target.checked })}
          style={{ width: 18, height: 18, accentColor: C.brassDk, cursor: "pointer" }}
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CalendarPage root
// ---------------------------------------------------------------------------

export function CalendarPage() {
  const navigate = useNavigate();
  const todayKey = useTodayKey();
  const { me } = useCurrentUser();

  // Tweaks: seeded from me.preferences.calendar (with TWEAK_DEFAULTS for any
  // missing field). useState's initializer captures whatever `me` is on the
  // first render; the re-seed effect below corrects if /api/me resolves
  // later (e.g. first visit, cold cache).
  const [tweaks, setTweaks] = useState<CalendarTweaks>(() => ({
    defaultView: me?.preferences?.calendar?.defaultView ?? TWEAK_DEFAULTS.defaultView,
    showPast: me?.preferences?.calendar?.showPast ?? TWEAK_DEFAULTS.showPast,
  }));
  const [view, setView] = useState<CalendarView>(tweaks.defaultView);
  const [activeCats, setActiveCats] = useState<Set<string>>(new Set());
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [tweaksOpen, setTweaksOpen] = useState(false);

  // Fetch state for the events list. `events === null` while pending; `[]`
  // is a legitimate empty result (no future or recent past entries).
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiFetch<CalendarListResponse>("/api/calendar/events")
      .then((res) => {
        if (cancelled) return;
        setEvents(res.events);
        setSyncedAt(res.syncedAt);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof ApiError ? e.message : "Couldn't load the calendar.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-sync the active view when the tweaked default changes. Mirrors Cut 1.
  useEffect(() => {
    setView(tweaks.defaultView);
  }, [tweaks.defaultView]);

  // Re-seed tweaks if /api/me resolves after first render. fromServerRef
  // gates the subsequent PATCH effect so the round-trip doesn't echo a
  // server read straight back to the server.
  const fromServerRef = useRef(true);
  const serverDefaultView = me?.preferences?.calendar?.defaultView;
  const serverShowPast = me?.preferences?.calendar?.showPast;
  useEffect(() => {
    fromServerRef.current = true;
    setTweaks({
      defaultView: serverDefaultView ?? TWEAK_DEFAULTS.defaultView,
      showPast: serverShowPast ?? TWEAK_DEFAULTS.showPast,
    });
  }, [serverDefaultView, serverShowPast]);

  // PATCH tweaks back to me.preferences.calendar on change, debounced 500ms
  // so a user dragging through both controls doesn't spam the endpoint.
  // fromServerRef = true means "this state update came from a server read,
  // not a user gesture" -- skip the PATCH and reset the flag.
  useEffect(() => {
    if (fromServerRef.current) {
      fromServerRef.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      apiFetch("/api/me/preferences", {
        method: "PUT",
        body: JSON.stringify({ calendar: { defaultView: tweaks.defaultView, showPast: tweaks.showPast } }),
      }).catch(() => {
        /* silent: the user's local choice still applies for this session,
           and the next reload (or the next successful save) will reconcile. */
      });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [tweaks.defaultView, tweaks.showPast]);

  // Filter pipeline: 30-day past horizon -> showPast tweak -> category filter.
  const cutoffKey = useMemo(() => addDays(todayKey, -PAST_HORIZON_DAYS), [todayKey]);

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    return events
      .filter((e) => e.end >= cutoffKey)
      .filter((e) => (tweaks.showPast ? true : e.end >= todayKey))
      .filter((e) => (activeCats.size === 0 ? true : activeCats.has(e.category)))
      .slice()
      .sort((a, b) => a.start.localeCompare(b.start));
  }, [events, activeCats, tweaks.showPast, cutoffKey, todayKey]);

  // List view orders upcoming events first, then past below in reverse chrono
  // (gotcha #14).
  const listEvents = useMemo(() => {
    const future = filteredEvents.filter((e) => e.end >= todayKey);
    const past = filteredEvents.filter((e) => e.end < todayKey).reverse();
    return [...future, ...past];
  }, [filteredEvents, todayKey]);

  const onTapEvent = useCallback((e: CalendarEvent) => setOpenEventId(e.id), []);

  if (openEventId) {
    return <EventDetail eventId={openEventId} onBack={() => setOpenEventId(null)} />;
  }

  // Reflect "no rows of interest" vs "data hasn't arrived" vs "error". Events
  // is null until the fetch resolves; loading is true during the same window.
  // After resolution, events may be an empty array (currently likely -- the
  // live Events DB has only 2 surfaced rows at the time of Cut 2 ship).
  const hasFinishedLoading = !loading && !loadError && events !== null;
  const visibleEmpty = hasFinishedLoading && events.length === 0;
  const filteredEmpty =
    hasFinishedLoading && events.length > 0 && filteredEvents.length === 0;

  return (
    <div>
      <div style={{ position: "relative" }}>
        <TopBar
          title="Calendar"
          sub="Events at NYMR"
          onBack={() => navigate("/apps")}
          right={
            <button
              type="button"
              aria-label="Calendar tweaks"
              onClick={() => setTweaksOpen((o) => !o)}
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                border: `1px solid ${C.hairline}`,
                background: tweaksOpen ? C.brassBg : C.card,
                color: C.brassDk,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: C.shadowS,
                flexShrink: 0,
              }}
            >
              <Icons.Settings s={16} c={C.brassDk} />
            </button>
          }
        />
        {tweaksOpen && (
          <TweaksPopover
            tweaks={tweaks}
            setTweaks={setTweaks}
            onClose={() => setTweaksOpen(false)}
          />
        )}
      </div>

      <div style={{ padding: "14px 16px 32px", display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Loading state -- match the WeeklyPage skeleton pattern. */}
        {loading && <CalendarSkeleton />}

        {/* Network error -- inline retry would be nicer; ship-and-iterate. */}
        {!loading && loadError && <CalendarErrorCard message={loadError} />}

        {/* Loaded with content -- normal render path. */}
        {hasFinishedLoading && !visibleEmpty && (
          <>
            <ViewToggle view={view} setView={setView} />
            <FilterStrip
              activeCats={activeCats}
              setActiveCats={setActiveCats}
              events={filteredEvents}
            />
            {filteredEmpty ? (
              <FilteredEmptyCard />
            ) : view === "list" ? (
              <ListView events={listEvents} today={todayKey} onTapEvent={onTapEvent} />
            ) : (
              <MonthView events={filteredEvents} today={todayKey} onTapEvent={onTapEvent} />
            )}
          </>
        )}

        {/* Loaded but zero events at all from the API. */}
        {visibleEmpty && <EmptyCard />}

        {/* Footer -- syncedAt + count. Renders below every state including
            loading (the "Awaiting first sync" copy stands in for a real
            timestamp until cal.SyncLog has its first info row). */}
        <div
          style={{
            textAlign: "center",
            padding: "4px 0",
            fontFamily: F.serif,
            fontStyle: "italic",
            fontSize: 11.5,
            color: C.inkMute,
            fontWeight: 600,
            letterSpacing: 0.3,
          }}
        >
          {hasFinishedLoading ? (
            <>
              {syncedAt ? `Last synced ${formatRelative(syncedAt)}` : "Awaiting first sync"}
              {" · "}
              {filteredEvents.length} event{filteredEvents.length === 1 ? "" : "s"} in range
            </>
          ) : loading ? (
            "Loading…"
          ) : (
            "Couldn't reach the sync log"
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading / error / empty cards
// ---------------------------------------------------------------------------

function CalendarSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SkeletonRow h={44} />
      <SkeletonRow h={36} />
      <SkeletonRow h={92} />
      <SkeletonRow h={92} />
      <SkeletonRow h={92} />
    </div>
  );
}

function SkeletonRow({ h }: { h: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height: h,
        background: C.paperWarm,
        borderRadius: 14,
        border: `1px solid ${C.hairlineLt}`,
      }}
    />
  );
}

function CalendarErrorCard({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        background: C.signalBg,
        border: "1px solid rgba(184,58,43,0.25)",
        borderRadius: 14,
        padding: 16,
        fontFamily: F.body,
        fontWeight: 700,
        color: C.signalDk,
      }}
    >
      Couldn't load the calendar.
      <div style={{ marginTop: 4, fontWeight: 600, fontSize: 12, color: C.inkSoft }}>
        {message}
      </div>
    </div>
  );
}

function EmptyCard() {
  return (
    <div
      style={{
        background: C.sageBg,
        border: `1px solid ${C.sageLt}`,
        borderRadius: 14,
        padding: 18,
        textAlign: "center",
        fontFamily: F.serif,
        fontSize: 16,
        color: C.sageDk,
      }}
    >
      No events scheduled.
      <div
        style={{
          fontSize: 12.5,
          color: C.inkSoft,
          fontWeight: 600,
          marginTop: 6,
          fontStyle: "italic",
          lineHeight: 1.4,
        }}
      >
        The Events team adds and updates events at events.nymr.co.uk;
        the calendar refreshes once a day.
      </div>
    </div>
  );
}

function FilteredEmptyCard() {
  return (
    <div
      style={{
        background: C.paperSoft,
        border: `1px solid ${C.hairlineLt}`,
        borderRadius: 14,
        padding: 20,
        textAlign: "center",
        fontFamily: F.serif,
        fontStyle: "italic",
        fontSize: 15,
        color: C.inkMute,
      }}
    >
      No events match your filter
    </div>
  );
}

// "Last synced 3h ago" / "less than an hour ago" / "2 days ago". Same shape
// as WeeklyPage.tsx's formatRelative -- could promote into a shared util if
// a third surface wants it.
function formatRelative(iso: string | null): string {
  if (!iso) return "at an unknown time";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "at an unknown time";
  const diffMs = Date.now() - ms;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
